import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LuZap, LuHistory } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import SectionHeader from '../../components/ui/SectionHeader/SectionHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import StatBlock from '../../components/ui/StatBlock/StatBlock';
import CurrencyAmount from '../../components/ui/CurrencyAmount/CurrencyAmount';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ChargingSessionCard from '../../components/domain/ChargingSessionCard/ChargingSessionCard';
import { useEnergyInsights } from '../../hooks/useEnergyInsights';
import { adaptAccountSessions } from '../../utils/accountSessionRecord';
import MonthSelector from './components/MonthSelector';
import MonthChart from './components/MonthChart';
import './EnergyInsightsPage.less';

/**
 * Energy Insights — monthly kWh + what the driver paid + recent
 * sessions view.
 *
 * The page is read-only. `/history` remains the durable list; this
 * page's "View all" link forwards there so we never duplicate detail
 * navigation logic.
 *
 * Data is authoritative-from-server: no localStorage rehydration, no
 * device-scoped history. The account API scopes by JWT user id.
 */
function EnergyInsightsPage() {
  const { month, data, loading, error, setMonth, refresh } = useEnergyInsights();

  const isInitialLoad = loading && !data && !error;

  const summary = data?.summary || {
    energyKWh: 0, sessionCount: 0, durationMinutes: 0, totalCost: 0, averageEnergyKWh: 0,
  };
  const currency = 'BBD'; // Barbados-only rollout; matches existing app-wide default.

  // Recent sessions ride through the same adapter the history page uses
  // so ChargingSessionCard renders identically to /history.
  const recentSessions = useMemo(
    () => adaptAccountSessions(data?.recentSessions || []),
    [data?.recentSessions],
  );

  const durationHours = Math.floor(summary.durationMinutes / 60);
  const durationMinutes = summary.durationMinutes % 60;
  const durationValue = summary.durationMinutes >= 60
    ? `${durationHours}h ${durationMinutes}m`
    : `${summary.durationMinutes}m`;

  const monthHasActivity = summary.sessionCount > 0;

  return (
    <Page className="gc-insights-page">
      <PageHeader
        title="Energy insights"
        subtitle="How much you've charged this month."
      />

      <Card className="gc-insights-controls">
        <MonthSelector
          month={month}
          onChange={setMonth}
          disabled={loading}
        />
      </Card>

      {/* Error — retryable, keeps the previous month visible. */}
      {!isInitialLoad && error && (
        <Card>
          <ErrorState
            title="We couldn't load your energy insights"
            description="Please check your connection and try again."
            onRetry={refresh}
          />
        </Card>
      )}

      {/* Initial skeleton — chart + summary placeholders. */}
      {isInitialLoad && (
        <div className="gc-insights-loading" aria-busy="true" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Loaded content. */}
      {!isInitialLoad && !error && data && (
        <>
          <Card className="gc-insights-chart-card">
            <MonthChart daily={data.daily} currency={currency} />
          </Card>

          <section aria-labelledby="insights-summary">
            <SectionHeader title={<span id="insights-summary">This month</span>} />
            <div className="gc-insights-stats">
              <StatBlock
                label="Energy"
                value={summary.energyKWh.toFixed(1)}
                unit="kWh"
                tone={monthHasActivity ? 'emphasis' : 'default'}
              />
              <StatBlock
                label="Sessions"
                value={String(summary.sessionCount)}
              />
              <StatBlock
                label="Time charging"
                value={durationValue}
              />
              {/* `summary.totalCost` is what was captured from this
                  driver's own card, less refunds (sw-ev-api
                  sqlCustomerPaidAmount). Charging billed to a client or
                  fleet account, or provided at no cost, adds nothing to
                  it — so it is labelled as the driver's payments, not
                  as the value of the month's charging. */}
              <StatBlock
                label="You paid"
                value={
                  <CurrencyAmount value={summary.totalCost} currency={currency} />
                }
              />
              {monthHasActivity && (
                <StatBlock
                  label="Avg / session"
                  value={summary.averageEnergyKWh.toFixed(1)}
                  unit="kWh"
                />
              )}
            </div>
          </section>

          {monthHasActivity ? (
            <section aria-labelledby="insights-recent">
              <SectionHeader
                title={<span id="insights-recent">Recent sessions</span>}
                action={<Link to="/history">View all</Link>}
              />
              {recentSessions.length === 0 ? (
                <Card>
                  <EmptyState
                    icon={<LuHistory />}
                    title="No completed sessions to show"
                    description="This month's charging activity is being finalised."
                  />
                </Card>
              ) : (
                <div className="gc-insights-recent">
                  {recentSessions.map((s) => (
                    <ChargingSessionCard
                      key={s.sessionUid}
                      session={s}
                      to={`/history/${encodeURIComponent(s.sessionUid)}`}
                    />
                  ))}
                </div>
              )}
            </section>
          ) : (
            <Card>
              <EmptyState
                icon={<LuZap />}
                title="No charging activity this month"
                description="Your completed charging sessions will appear here."
                action={
                  <Button as={Link} to="/charge" leadingIcon={<LuZap />}>
                    Find a charger
                  </Button>
                }
              />
            </Card>
          )}
        </>
      )}
    </Page>
  );
}

export default EnergyInsightsPage;
